/* Product finder quiz (sections/product-finder.liquid).
   One step at a time: an answer records its products and moves on; «Back» steps back and forgets
   that step's answer. After the last step the products most answers pointed to are fetched as
   cards from the server (/search?type=product&section_id=…&q=id:… OR …), ordered by how many
   answers chose them. The progress bar follows the steps. */

class MwProductFinder extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    this.steps = [...this.querySelectorAll('[data-finder-step]')];
    this.result = this.querySelector('[data-finder-result]');
    this.gate = this.querySelector('[data-finder-gate]');
    this.gatePassed = false;
    this.progress = this.querySelector('[data-finder-progress]');
    this.answers = [];
    this.addEventListener('click', (event) => {
      const answer = event.target.closest('[data-finder-answer]');
      if (answer) return this.choose(answer);
      if (event.target.closest('[data-finder-skip]')) return this.passGate();
      if (event.target.closest('[data-finder-back]')) return this.back();
      if (event.target.closest('[data-finder-restart]')) return this.show(0, true);
    });
    // The email step: sign up in the background, then show the result whatever the answer was —
    // a failed signup must never stand between the shopper and the products.
    this.querySelector('[data-finder-gate-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      const form = event.currentTarget;
      if (!form.reportValidity()) return;
      fetch(form.action, { method: 'POST', body: new FormData(form) }).catch(() => {});
      this.passGate();
    });
    this.show(0, true);
  }

  passGate() {
    this.gatePassed = true;
    this.show(this.steps.length);
  }

  show(index, reset = false) {
    if (reset) {
      this.answers = [];
      this.gatePassed = false;
    }
    this.index = index;
    const total = this.steps.length;
    const gated = index >= total && this.gate && !this.gatePassed;
    if (this.gate) this.gate.hidden = !gated;
    this.steps.forEach((step, i) => {
      step.hidden = i !== index;
      const back = step.querySelector('[data-finder-back]');
      if (back) back.hidden = i === 0;
    });
    if (this.result) this.result.hidden = index < total || gated;
    const percent = Math.round((Math.min(index, total) / total) * 100);
    this.progress?.style.setProperty('--pfind-progress', `${percent}%`);
    this.progress?.setAttribute('aria-valuenow', String(percent));
    const current = index < total ? this.steps[index] : gated ? this.gate : this.result;
    // querySelector takes the first match in document order, so the gate names its field itself.
    const focusTarget = gated
      ? current?.querySelector('input[type="email"]')
      : current?.querySelector('[data-finder-answer], h3');
    focusTarget?.focus?.({ preventScroll: true });
    if (index >= total && !gated) this.recommend();
  }

  choose(answer) {
    this.answers[this.index] = (answer.dataset.products || '').split(',').filter(Boolean);
    this.show(this.index + 1);
  }

  back() {
    this.answers.length = Math.max(0, this.index - 1);
    this.show(Math.max(0, this.index - 1));
  }

  async recommend() {
    const target = this.querySelector('[data-finder-target]');
    const empty = this.querySelector('[data-finder-empty]');
    if (!target) return;
    target.replaceChildren();
    if (empty) empty.hidden = true;

    const score = new Map();
    this.answers.flat().forEach((id) => score.set(id, (score.get(id) || 0) + 1));
    const limit = Number(this.dataset.limit) || 3;
    const ids = [...score.keys()].sort((a, b) => score.get(b) - score.get(a)).slice(0, limit);
    if (!ids.length) {
      if (empty) empty.hidden = false;
      return;
    }

    try {
      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const response = await fetch(`${this.dataset.searchUrl}${encodeURIComponent(query)}`, {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(response.statusText);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const list = parsed.querySelector('[data-finder-cards]');
      if (!list) throw new Error('no cards');
      // Search orders by relevance; the finder's order is by how many answers chose a product.
      const cards = [...list.children].sort(
        (a, b) => ids.indexOf(a.dataset.productId) - ids.indexOf(b.dataset.productId)
      );
      list.replaceChildren(...cards);
      target.replaceChildren(list);
      window.Munkwell?.motion?.scan?.(target);
    } catch {
      if (empty) empty.hidden = false;
    }
  }
}

if (!customElements.get('mw-product-finder')) customElements.define('mw-product-finder', MwProductFinder);
